import {Inject, Injectable} from '@angular/core';

import {Observable, Subject} from "rxjs";

import {environment} from "@src/environments/environment";

import {WebSocketWrapper} from "@commons/models/other/web-socket-wrapper";
import {BusinessEvent} from "@commons/models/domain/BusinessEvent";

import {LogInApiHttpService} from '../services-http/log-in-api-http.service';

import {Vehicle} from '@commons/models/domain/Vehicle';
import {Driver} from "@commons/models/domain/Driver";

import {WebSocketDataTypes} from "@shared/websocket/models/web-socket-data-types";
import {NewDataStoreService} from "@shared/services/new-data-store.service";
import {NewVehiclesHttpService} from "@shared/services-http/new-vehicles-http.service";
import {BaseService} from "@shared/services/base.service";
import {EventDataProcessor} from "@shared/websocket/processors/event-data-processor";
import {VehicleIndicatorsProcessor} from "@shared/websocket/processors/vehicle-indicators-processor";
import {VehicleLocationProcessor} from "@shared/websocket/processors/vehicle-location-processor";
import {VehicleTachoProcessor} from "@shared/websocket/processors/vehicle-tacho-processor";
import { TranslateService } from '@ngx-translate/core';
import { AppNotificationTypes } from '@commons/models/other/app-notification.model';

@Injectable({
  providedIn: 'root',
})
export class NewWebSocketHttpService extends BaseService {

  private readonly vehiclesDict = new Map<number, Vehicle>();
  private readonly driversDict = new Map<number, Driver>();
  private readonly driverElectronicIdToDriverIdDict = new Map<string, number>();

  private readonly onEventSubject = new Subject<BusinessEvent>();
  private readonly onEventSubjectData = new Subject<any>();
  private readonly onVehicleChangedSubject = new Subject<Vehicle[]>();

  private eventsWS?: WebSocketWrapper;
  private vehicleWS?: WebSocketWrapper;
  private routeName?: string;
  private updatedVehiclesList: Vehicle[] = [];
  private isLoggedInByLink = false;

  private eventDataProcessor = new EventDataProcessor();
  private vehicleIndicatorsProcessor = new VehicleIndicatorsProcessor();
  private vehicleLocationProcessor = new VehicleLocationProcessor();
  private vehicleTachoProcessor = new VehicleTachoProcessor();

  get eventNotification(): Observable<BusinessEvent> {
    return this.onEventSubject;
  }

  get eventChangeVehicleData(): Observable<any> {
    return this.onEventSubjectData;
  }

  get updatedVehicles(): Observable<Vehicle[]> {
    return this.onVehicleChangedSubject;
  }

  constructor(
    private newVehiclesService: NewVehiclesHttpService,
    private logInApiHttpService: LogInApiHttpService,
    private newDataStoreService: NewDataStoreService,
    @Inject(TranslateService) private translate: TranslateService
  ) {
    super();
  }

  start(
    sessionId: string,
    vehicles: Vehicle[],
    drivers: Driver[],
    createEventsWS: boolean,
    isLoggedInByLink: boolean
  ) {
    this.isLoggedInByLink = isLoggedInByLink;
    this.initializeDictionaries(vehicles, drivers);
    this.setDriversInVehicles();
    this.saveDictionariesInDataStore();
    this.startVehicleWS(sessionId);

    if (createEventsWS)
      this.startEventWS(sessionId);

    this.setIntervalForOnVehicleChangedSubject();
  }

  close() {
    this.vehicleWS?.close();
    this.eventsWS?.close();
  }

  changeHttpRoute(routeName: string) {
    this.routeName = routeName;

    if (this.vehicleWS)
      this.vehicleWS.changeHttpRoute(routeName);

    if (this.eventsWS)
      this.eventsWS.changeHttpRoute(routeName);
  }

  private setIntervalForOnVehicleChangedSubject() {
    setInterval(() => {
      this.onVehicleChangedSubject.next(this.updatedVehiclesList);
      this.updatedVehiclesList = [];
    }, 2000);
  }

  private initializeDictionaries(vehicles: Vehicle[], drivers: Driver[]) {
    vehicles.forEach(v => this.vehiclesDict.set(v.Id, v));

    drivers.forEach(d => {
      this.driversDict.set(d.Id, d);
      this.driverElectronicIdToDriverIdDict.set(d.EI, d.Id);
    });
  }

  private startEventWS(sessionId: string) {
    this.eventsWS = new WebSocketWrapper(environment.wsEventServiceUrl, 'SessionId=' + sessionId, this.routeName!);
    this.eventsWS.onerror = (event: Event) => this.logInApiHttpService.logError(event).subscribe();
    this.eventsWS.onmessage = (event: any) => this.newEventDataHandler(event);
  }

  private startVehicleWS(sessionId: string) {
    this.vehicleWS = new WebSocketWrapper(environment.wsVehicleDataChangeServiceUrl, 'SessionId=' + sessionId, this.routeName!);
    this.vehicleWS.onerror = (event: Event) => this.logInApiHttpService.logError(event).subscribe();
    this.vehicleWS.onmessage = (event: any) => {
      this.newVehicleDataHandler(event);
    };
  }

  private getVehicleById(vehicleId: number): Observable<Vehicle> {
    return new Observable<Vehicle>(observable => {
      const vehicle = this.vehiclesDict.get(vehicleId);
      if (vehicle) {
        observable.next(vehicle);
        observable.complete();
      } else {
        // TODO GB Jest to obejście problemu polegającego na tym że gdy ktoś zaloguje się za pomocą linka to
        // websocket błędnie wysyła id pojazdów których nie ma na liście pobieranej przy starcie aplikacji.
        // Próba pobrania pojazdu do którego użtywkonik oczywiście nie będzie mieć uprawnienia kończy się błędem
        if (this.isLoggedInByLink) {
          observable.next(new Vehicle());
          observable.complete();
        } else {
          console.warn("Pojazd nie jest na liście wszystkich pojazdów i zostanie pobrany przez ID=" + vehicleId);
          this.newVehiclesService.getById(vehicleId).subscribe(vehicle => {
            this.vehiclesDict.set(vehicleId, vehicle);
            observable.next(vehicle);
            observable.complete();
          });
        }
      }
    });
  }

  private updateVehicle(newData: any, vehicle: Vehicle) {
    switch (newData.DataType) {
      case WebSocketDataTypes.indicators:
        this.vehicleIndicatorsProcessor.setVehiclesIndicators(
          newData, vehicle, this.driversDict, this.driverElectronicIdToDriverIdDict);
        break;

      case WebSocketDataTypes.geo:
        this.vehicleLocationProcessor.setVehiclesLocation(newData, vehicle);
        break;

      case WebSocketDataTypes.tacho:
        this.vehicleTachoProcessor.setVehiclesTacho(newData, vehicle);
        break;
    }

    this.newDataStoreService.addOrUpdateVehicle(vehicle);
    this.updatedVehiclesList.push(vehicle);
  }

  private saveDictionariesInDataStore() {
    const vehicles = [...this.vehiclesDict.values()];
    this.newDataStoreService.saveVehicles(vehicles);

    const drivers = [...this.driversDict.values()];
    this.newDataStoreService.saveDrivers(drivers);
  }

  private newVehicleDataHandler(event: any) {
    const newData = JSON.parse(event.data);

    switch (newData.DataType) {
      case WebSocketDataTypes.indicators:
        this.getVehicleById(newData.Data.ID).subscribe(vehicle => this.updateVehicle(newData, vehicle));
        break;

      case WebSocketDataTypes.geo:
        this.getVehicleById(newData.Data.Id).subscribe(vehicle => this.updateVehicle(newData, vehicle));
        break;

      case WebSocketDataTypes.tacho:
        this.getVehicleById(newData.Data.IdVehicle).subscribe(vehicle => this.updateVehicle(newData, vehicle));
        break;
    }
  }

  private newEventDataHandler(messageEvent: MessageEvent) {
    let event = JSON.parse(messageEvent.data);
    console.log('newEventDataHandler', event);
    if (event.DataType === 3) {
      event = this.prepareEventDataForNotify(event);
      this.onEventSubject.next(this.eventDataProcessor.getEvent(event));
      this.onEventSubjectData.next(event);
    }
  }

  private prepareEventDataForNotify(event: any): any {
    const ifLockChanging = event?.Data?.O;
    if (ifLockChanging) {
      const isSuccessChanging = event?.Data?.St;
      return {
        ...event,
        Data: {
          ...event.Data,
          Nm: 'Zmiana statusu blokady', //TODO: this.translate.instant('changeOfLockStatus'),this.translate.instant('changeLockStatusSuccess'),this.translate.instant('changeLockStatusError')
          De: isSuccessChanging ? 'Status został pomyślnie zmieniony' : 'Wystąpił błąd podczas zmiany statusu blokady', 
        },
        ifLockChanging: true,
        type: isSuccessChanging ? AppNotificationTypes.APP_SUCCESS : AppNotificationTypes.APP_ERROR, 
      };
    }
    return event;
  }

  private setDriversInVehicles() {
    this.vehiclesDict.forEach(vehicle => {
      if (vehicle.DR) {
        const driver = this.driversDict.get(vehicle.DR);
        if (driver) {
          vehicle.LastDriver = this.getClone(driver);
          (driver as any).Status = vehicle.St; //TODO GB dodany cast na any żeby dało się ustawić tą nie istniejącą właściwość
        }
      }
    });
  }

}
